import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User, Tenant, LoginRequest } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeToSessionChanges } from '../utils/sessionSync';
import { authAPI, tenantsAPI, apiClient } from '../utils/api';
import { clearWebOfflineCaches } from '../utils/offlineStorage';
import { setTenantSlugCookie, clearTenantSlugCookie } from '../utils/cookies';

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  tenant: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => {
    throw new Error('AuthProvider not initialized');
  },
  logout: async () => {
    throw new Error('AuthProvider not initialized');
  },
  refreshToken: async () => {
    throw new Error('AuthProvider not initialized');
  },
  stopImpersonation: async () => {
    throw new Error('AuthProvider not initialized');
  },
});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const initializationStarted = useRef(false);
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Host users have user but no tenant
  const isAuthenticated = !!user;

  useEffect(() => {
    if (tenant?.id) apiClient.client.defaults.headers.common['X-Tenant-ID'] = tenant.id;
    else delete apiClient.client.defaults.headers.common['X-Tenant-ID'];
  }, [tenant?.id]);

  useEffect(() => {
    if (!user) return;
    const identity = JSON.stringify([user.id, tenant?.id || null, !!user.impersonating]);
    return subscribeToSessionChanges(identity, () => {
      // Another tab changed account/tenant: hide the old view before any reload.
      setIsLoading(true);
      setUser(null);
      setTenant(null);
      void queryClient.cancelQueries();
      queryClient.clear();
      void clearWebOfflineCaches().finally(() => window.location.replace('/'));
    });
  }, [user?.id, user?.impersonating, tenant?.id, queryClient]);

  // Role changes must reach already signed-in users without another login.
  useEffect(() => {
    if (!user?.id || user.impersonating) return;
    let active = true;
    let pending = false;
    const reloadPermissions = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        const response = await authAPI.getMe();
        if (active) setUser(response.data.user);
      } catch {
        // Authentication errors are handled by the API client's interceptor.
      } finally {
        pending = false;
      }
    };
    window.addEventListener('focus', reloadPermissions);
    document.addEventListener('visibilitychange', reloadPermissions);
    const interval = window.setInterval(reloadPermissions, 60_000);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', reloadPermissions);
      document.removeEventListener('visibilitychange', reloadPermissions);
    };
  }, [user?.id, user?.impersonating]);

  useEffect(() => {
    // Refresh tokens rotate: StrictMode must not consume the same token twice.
    if (initializationStarted.current) return;
    initializationStarted.current = true;
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          await refreshToken();
        } catch (error) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await authAPI.login(credentials);
      const data = response.data as unknown as { access_token: string; refresh_token: string; user: User; tenant: Tenant | null };
      const { access_token, refresh_token, user: userData, tenant: tenantData } = data;
      
      await queryClient.cancelQueries();
      queryClient.clear();
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      setUser(userData);
      setTenant(tenantData);

      // Ricorda il tenant per il prossimo login; se si entra come Host
      // (campo tenant vuoto) il cookie viene rimosso
      if (tenantData?.slug) {
        setTenantSlugCookie(tenantData.slug);
      } else {
        clearTenantSlugCookie();
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refresh_token = localStorage.getItem('refresh_token');
      await authAPI.logout(refresh_token || undefined);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear tokens and state
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      // Drop the offline visite snapshot so it does not outlive the session.
      await clearWebOfflineCaches();
      setUser(null);
      setTenant(null);

      // Redirect to login page
      window.location.href = '/login';
    }
  };

  const refreshToken = async () => {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await authAPI.refreshToken(refresh_token);
      const data = response.data as unknown as { access_token: string; refresh_token: string; user: User; tenant: Tenant | null };
      const { access_token, refresh_token: new_refresh_token, user: userData, tenant: tenantData } = data;

      localStorage.setItem('access_token', access_token);
      // Save new refresh token (token rotation)
      if (new_refresh_token) {
        localStorage.setItem('refresh_token', new_refresh_token);
      }
      setUser(userData);
      setTenant(tenantData);
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setTenant(null);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      // Call stop-impersonation endpoint to get back host tokens
      const response = await tenantsAPI.stopImpersonation();
      const data = response.data as unknown as { access_token: string; refresh_token: string; user: User; tenant: Tenant | null };
      const { access_token, refresh_token, user: userData, tenant: tenantData } = data;

      // Update tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      // Update state
      setUser(userData);
      setTenant(tenantData);

      // Leaving the impersonated tenant — drop its offline visite snapshot.
      await clearWebOfflineCaches();

      // Reload the page to reset all state
      window.location.href = '/';
    } catch (error) {
      console.error('Stop impersonation error:', error);
      // If stop impersonation fails, force logout
      await logout();
    }
  };

  const value = {
    user,
    tenant,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshToken,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
